import { useState } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { 
  Plus, 
  FileText, 
  Trophy, 
  AlertTriangle, 
  Lightbulb, 
  Target,
  Calendar,
  X,
  Clock,
  PlayCircle,
  Flag
} from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import type { EvidenceEntry } from '@/types/objectives'
import { sastDay, sastToday } from '@/components/admin/lifecycle/spellCoverage'

interface EvidencePanelProps {
  objectiveId: string
  objectiveTitle: string
  evidence: EvidenceEntry[]
  onAddEvidence: (evidence: Omit<EvidenceEntry, 'id' | 'createdBy' | 'updatedAt'>) => void
  initiatives?: Array<{ id: string; title: string; progress: number }>
  kpis?: Array<{ id: string; name: string; currentValue?: number; targetValue?: number }>
  isCollapsed?: boolean
}

const evidenceTypes = [
  { type: 'achievement', icon: Trophy, color: 'emerald', label: 'Achievement' },
  { type: 'challenge', icon: AlertTriangle, color: 'amber', label: 'Challenge' },
  { type: 'insight', icon: Lightbulb, color: 'blue', label: 'Insight' },
  { type: 'milestone', icon: Target, color: 'purple', label: 'Milestone' }
] as const

export default function EvidencePanel({ 
  objectiveId, 
  objectiveTitle, 
  evidence, 
  onAddEvidence,
  initiatives = [],
  kpis = [],
  isCollapsed = false 
}: EvidencePanelProps) {
  const [isExpanded, setIsExpanded] = useState(!isCollapsed)
  const [activeType, setActiveType] = useState<'achievement' | 'challenge' | 'insight' | 'milestone'>('achievement')
  const [showAddForm, setShowAddForm] = useState(false)
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    impact: '',
    initiativeId: '',
    kpiId: '',
    dueDate: '',
    actionPlan: '',
    priority: 'medium' as 'high' | 'medium' | 'low'
  })


  const handleSubmit = () => {
    if (!formData.title.trim()) return

    onAddEvidence({
      objectiveId,
      type: activeType,
      title: formData.title,
      description: formData.description,
      impact: formData.impact,
      evidence: formData.description, // Could be enhanced with file uploads
      date: new Date().toISOString(),
      initiativeId: formData.initiativeId || undefined,
      kpiId: formData.kpiId || undefined,
      // Enhanced milestone fields
      dueDate: activeType === 'milestone' ? formData.dueDate || undefined : undefined,
      actionPlan: activeType === 'milestone' ? formData.actionPlan || undefined : undefined,
      priority: activeType === 'milestone' ? formData.priority : undefined,
      status: activeType === 'milestone' ? 'pending' : undefined
    })

    setFormData({ title: '', description: '', impact: '', initiativeId: '', kpiId: '', dueDate: '', actionPlan: '', priority: 'medium' })
    setShowAddForm(false)
  }

  const evidenceByType = evidenceTypes.map(type => ({
    ...type,
    entries: evidence.filter(e => e.type === type.type)
  }))

  if (!isExpanded) {
    return (
      <Card className="border border-gray-200 hover:border-gray-300 transition-colors">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="h-5 w-5 text-gray-600" />
              <div>
                <h4 className="font-medium text-gray-900">Impact Portfolio</h4>
                <p className="text-sm text-gray-500">{evidence.length} entries</p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(true)}
              className="text-gray-600 hover:text-gray-900"
            >
              View
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: 'auto' }}
      className="space-y-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText className="h-5 w-5 text-gray-700" />
          <div>
            <h3 className="font-semibold text-gray-900">Impact Portfolio</h3>
            <div className="flex items-center gap-3">
              <p className="text-sm text-gray-600">{objectiveTitle}</p>
              <div className="flex items-center gap-2">
                {evidence.filter(e => e.initiativeId).length > 0 && (
                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                    {evidence.filter(e => e.initiativeId).length} linked to initiatives
                  </Badge>
                )}
                {evidence.filter(e => e.kpiId).length > 0 && (
                  <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                    {evidence.filter(e => e.kpiId).length} linked to KPIs
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddForm(!showAddForm)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Entry
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(false)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Add Entry Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <Card className="border-2 border-blue-200 bg-blue-50/50">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-2">
                  <h4 className="font-medium text-gray-900">Add New Entry</h4>
                </div>
                
                {/* Type Selection */}
                <div className="flex gap-1 p-1 bg-white rounded-lg">
                  {evidenceTypes.map(({ type, icon: Icon, color, label }) => (
                    <button
                      key={type}
                      onClick={() => setActiveType(type)}
                      className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                        activeType === type
                          ? `bg-${color}-100 text-${color}-700 shadow-sm`
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>

                {/* Form Fields */}
                <div className="space-y-3">
                  <Input
                    placeholder="Entry title..."
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    className="font-medium"
                  />
                  <Input
                    placeholder="Description and details..."
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <Input
                    placeholder="Impact on objective..."
                    value={formData.impact}
                    onChange={(e) => setFormData(prev => ({ ...prev, impact: e.target.value }))}
                  />

                  {/* Enhanced fields for milestones and action plans */}
                  {activeType === 'milestone' && (
                    <>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Due Date</label>
                          <Input
                            type="date"
                            value={formData.dueDate}
                            onChange={(e) => setFormData(prev => ({ ...prev, dueDate: e.target.value }))}
                            className="h-8 text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-600 mb-1 block">Priority</label>
                          <Select value={formData.priority} onValueChange={(value: 'high' | 'medium' | 'low') => setFormData(prev => ({ ...prev, priority: value }))}>
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="high">
                                <div className="flex items-center gap-2">
                                  <Flag className="h-3 w-3 text-red-500" />
                                  High Priority
                                </div>
                              </SelectItem>
                              <SelectItem value="medium">
                                <div className="flex items-center gap-2">
                                  <Flag className="h-3 w-3 text-yellow-500" />
                                  Medium Priority
                                </div>
                              </SelectItem>
                              <SelectItem value="low">
                                <div className="flex items-center gap-2">
                                  <Flag className="h-3 w-3 text-green-500" />
                                  Low Priority
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Action Plan</label>
                        <Input
                          placeholder="Specific actions needed to achieve this milestone..."
                          value={formData.actionPlan}
                          onChange={(e) => setFormData(prev => ({ ...prev, actionPlan: e.target.value }))}
                          className="text-sm"
                        />
                      </div>
                    </>
                  )}
                  
                  {/* Link to Initiatives and KPIs */}
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-gray-200">
                    {initiatives.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Link to Initiative</label>
                        <Select value={formData.initiativeId} onValueChange={(value) => setFormData(prev => ({ ...prev, initiativeId: value }))}>
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Select initiative..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">None</SelectItem>
                            {initiatives.map((initiative) => (
                              <SelectItem key={initiative.id} value={initiative.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span className="truncate">{initiative.title}</span>
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {initiative.progress}%
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {kpis.length > 0 && (
                      <div>
                        <label className="text-xs font-medium text-gray-600 mb-1 block">Link to KPI</label>
                        <Select value={formData.kpiId} onValueChange={(value) => setFormData(prev => ({ ...prev, kpiId: value }))}>
                          <SelectTrigger className="h-8 text-sm">
                            <SelectValue placeholder="Select KPI..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="">None</SelectItem>
                            {kpis.map((kpi) => (
                              <SelectItem key={kpi.id} value={kpi.id}>
                                <div className="flex items-center justify-between w-full">
                                  <span className="truncate">{kpi.name}</span>
                                  <Badge variant="outline" className="ml-2 text-xs">
                                    {kpi.currentValue ?? 0}/{kpi.targetValue ?? 0}
                                  </Badge>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSubmit}
                    disabled={!formData.title.trim()}
                  >
                    Add Entry
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Evidence Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {evidenceByType.map(({ type, icon: Icon, color, label, entries }) => (
          <Card key={type} className="border border-gray-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-lg bg-${color}-100`}>
                    <Icon className={`h-4 w-4 text-${color}-600`} />
                  </div>
                  <span className="text-sm font-medium text-gray-700">{label}</span>
                </div>
                <Badge variant="secondary" className="text-xs">
                  {entries.length}
                </Badge>
              </div>

              <div className="space-y-2 max-h-40 overflow-y-auto">
                {entries.map((entry) => {
                  // A due date is a plain day: overdue once that South African day
                  // has passed, due soon from today until a week ahead.
                  const due = entry.type === 'milestone' ? sastDay(entry.dueDate) : null
                  const today = sastToday()
                  const isOverdue = !!due && due < today
                  const isDueSoon = !!due && due >= today && due <= sastToday(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000))
                  
                  return (
                    <div key={entry.id} className={`p-2 rounded-md ${
                      entry.type === 'milestone' 
                        ? isOverdue 
                          ? 'bg-red-50 border border-red-200' 
                          : isDueSoon 
                            ? 'bg-yellow-50 border border-yellow-200'
                            : 'bg-purple-50 border border-purple-200'
                        : 'bg-gray-50'
                    }`}>
                      <div className="flex items-start justify-between mb-1">
                        <h5 className="text-xs font-medium text-gray-900 flex-1">
                          {entry.title}
                        </h5>
                        {entry.type === 'milestone' && (
                          <div className="flex items-center gap-1 ml-2">
                            {entry.priority === 'high' && <Flag className="h-3 w-3 text-red-500" />}
                            {entry.priority === 'medium' && <Flag className="h-3 w-3 text-yellow-500" />}
                            {entry.priority === 'low' && <Flag className="h-3 w-3 text-green-500" />}
                            {isOverdue && <Clock className="h-3 w-3 text-red-500" />}
                            {isDueSoon && <Clock className="h-3 w-3 text-yellow-500" />}
                          </div>
                        )}
                      </div>
                      
                      <p className="text-xs text-gray-600 line-clamp-2 mb-2">
                        {entry.description}
                      </p>

                      {/* Enhanced milestone information */}
                      {entry.type === 'milestone' && (
                        <>
                          {entry.actionPlan && (
                            <div className="mb-2 p-1.5 bg-white/80 rounded border border-gray-200">
                              <div className="flex items-center gap-1 mb-1">
                                <PlayCircle className="h-3 w-3 text-blue-500" />
                                <span className="text-xs font-medium text-gray-700">Action Plan</span>
                              </div>
                              <p className="text-xs text-gray-600">{entry.actionPlan}</p>
                            </div>
                          )}
                          {entry.dueDate && (
                            <div className="flex items-center gap-1 mb-2">
                              <Calendar className="h-3 w-3 text-gray-400" />
                              <span className={`text-xs ${
                                isOverdue ? 'text-red-600 font-medium' : 
                                isDueSoon ? 'text-yellow-600 font-medium' : 
                                'text-gray-500'
                              }`}>
                                Due: {new Date(entry.dueDate).toLocaleDateString()}
                                {isOverdue && ' (Overdue)'}
                                {isDueSoon && ' (Due Soon)'}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                      
                      {/* Connected Initiative/KPI Indicators */}
                      <div className="flex items-center gap-1 mb-2">
                        {entry.initiativeId && (
                          <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                            Initiative
                          </Badge>
                        )}
                        {entry.kpiId && (
                          <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700 border-purple-200">
                            KPI
                          </Badge>
                        )}
                      </div>
                      
                      {entry.type !== 'milestone' && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-gray-400" />
                          <span className="text-xs text-gray-500">
                            {new Date(entry.date).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                  )
                })}
                {entries.length === 0 && (
                  <div className="text-center py-3">
                    <p className="text-xs text-gray-500">No {label.toLowerCase()}s yet</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </motion.div>
  )
}